import React, { forwardRef } from 'react'
import styled from 'styled-components'

import { useResolveSizeLayout } from '../../../hooks/blockStyle/size'
import { flexMinSizeStyle, getPositionStyle, getSizeStyle } from '../../../utils'
import { useContainerLayoutContext, useFlowLayoutContext } from '../Context'
import type { FlowLayoutNode } from '../types'

type StyledProps = {
    disabled?: boolean
}

const StyledNode = styled.div<StyledProps>`
    flex-shrink: 0;
    position: relative;

    &[data-highlight='true'] {
        background-color: #5551ff1f;
        outline: 2px solid var(--color-main);
        outline-offset: -1px;
    }
`

export interface FlowLayoutCustomNodeProps extends React.ComponentPropsWithoutRef<'div'> {
    data: FlowLayoutNode
    disabled?: boolean
    extra?: React.ReactNode
    onDataDrawerVisible?: (val: boolean) => void
}

export const CustomNode = forwardRef<HTMLDivElement, FlowLayoutCustomNodeProps>((props, ref) => {
    const { data, style, disabled: propDisabled, onDataDrawerVisible, children, extra, ...rest } = props
    const { size, position } = data.data ?? {}
    const parentLayoutCtx = useContainerLayoutContext()
    const { highlight, nodeRender: NodeRender = React.Fragment, sortable } = useFlowLayoutContext()
    const resolvedSize = useResolveSizeLayout(size)

    const mergeStyle: React.CSSProperties = {
        ...flexMinSizeStyle,
        ...getSizeStyle(resolvedSize, parentLayoutCtx.layout?.align?.direction, sortable),
        ...style,
        ...getPositionStyle(position, parentLayoutCtx.level)
    }

    return (
        <StyledNode
            ref={ref}
            style={mergeStyle}
            data-type="custom"
            data-node-id={data.id}
            data-highlight={!!highlight?.[data.id]?.self}
            {...rest}
        >
            <NodeRender node={data}>
                {children}
                {extra}
            </NodeRender>
        </StyledNode>
    )
})
